import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { PlacesTypeahead } from '@/design-system/atoms/Forms/PlacesTypeahead';
import { TextField } from '@/design-system/atoms/Forms/TextField';
import { Typeahead } from '@/design-system/atoms/Forms/Typeahead';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { IconList } from '@/design-system/atoms/Icons/IconList';
import { IconMap } from '@/design-system/atoms/Icons/IconMap';
import { IconSliders } from '@/design-system/atoms/Icons/IconSliders';
import { Image } from '@/design-system/atoms/Image';
import { Text } from '@/design-system/atoms/Text';
import { Title } from '@/design-system/atoms/Title';
import IllustrationPlayer from '@/design-system/molecules/IllustrationPlayer';
import { SearchBar } from '@/design-system/molecules/SearchBar';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSearchParams } from '@/hooks/useSearchParams';
import { trackInteraction } from '@/utils/analytics';
import { locationCheck } from '@/utils/locationCheck';

import { useLocationFilterContext } from '../../../contexts/LocationFilterContext';
import { useLocationSearchContext } from '../../../contexts/LocationSearchContext';
import { useLocationUIContext } from '../../../contexts/LocationUIContext';
import { useLocationViewState } from '../../../hooks/useLocationViewState';
import { formValidation } from '../../../utils/formValidation';
import { LocationFilter } from '../../LocationFilter';
import styles from '../LocationHero.module.scss';

const LocationHeroResults = ({
    inputNameTypeahead,
    inputNameZipCity,
    searchValue,
    setSearchValue,
    onSelectionItemChange,
    onSelectionZipCityChange,
    typeaheadItems,
    typeaheadFixedItem,
    zipCityValue,
    setZipCityValue,
    onSubmit,
    submitDisabled,
    mapCta,
    illustration,
    image,
    googleMaps,
    placeSuggestions,
    handleZipCityInputChange,
    zipInvalid,
    setZipInvalid,
    handleAutoSelect,
}) => {
    const {
        title,
        description,
        searchLabel,
        searchErrorMessage,
        zipLabel,
        zipErrorMessage,
        filterLabel,
        filterAriaLabel,
        isLocationCategory,
        smallBreakpoint,
    } = useLocationUIContext();

    const { handleShowResultList, handleShowMaps } = useLocationSearchContext();

    const viewState = useLocationViewState();

    const searchParams = useSearchParams();

    const breakpoints = useBreakpoint();
    const isMobile = !breakpoints?.large;
    const searchRef = useRef();
    const zipRef = useRef();
    const [zipSR, setZipSR] = useState(false);
    const [searchValueInvalid, setSearchValueInvalid] = useState(false);
    const [searchValueError, setSearchValueError] = useState();
    const [zipError, setZipError] = useState();
    const { openPanel, setOpenPanel } = useLocationFilterContext();

    const filterPanelOpen = useCallback(() => {
        setOpenPanel(true);
        //analytics
        trackInteraction({
            componentName: 'Location Hero',
            interactionType: 'cta',
            componentTitle: title,
            selector: 'button',
            actionLabel: mapCta?.label || 'Map View',
            searchTerm: searchValue,
            zipCode: !isNaN(zipCityValue) ? zipCityValue : '',
        });
    }, [setOpenPanel, searchValue, zipCityValue, title, mapCta]);

    const submitForm = useCallback(
        async (e) => {
            e.preventDefault();

            const { isGeocoded, newLocation } = await locationCheck({
                zipCityValue,
                suggestions: placeSuggestions,
            });

            const [isValid, keys, message] = formValidation(
                !isLocationCategory
                    ? {
                          searchValue,
                          zipCityValue: newLocation || zipCityValue,
                          isGeocoded: isGeocoded,
                      }
                    : {
                          zipCityValue: newLocation || zipCityValue,
                          isGeocoded: isGeocoded,
                      },
                !isLocationCategory
                    ? { searchValue: searchErrorMessage, zipValue: zipErrorMessage }
                    : { zipCityValue: zipErrorMessage },
            );

            if (isValid) {
                setSearchValueInvalid(false);
                setZipInvalid(false);

                onSubmit({
                    term: searchValue,
                    zipCityValue: newLocation || zipCityValue,
                    viewMode: searchParams.get('view-mode'),
                });

                //analytics
                trackInteraction({
                    eventName: 'search',
                    componentName: 'Location Hero',
                    interactionType: 'search_results',
                    componentTitle: title,
                    selector: 'search',
                    searchTerm: searchValue,
                    zipCode: !isNaN(zipCityValue) ? zipCityValue : '',
                });

                return;
            }

            if (keys.includes('zipCityValue') && keys.includes('searchValue')) {
                setZipSR(true);
                setZipInvalid(true);
                setSearchValueInvalid(true);
                setZipError(message);
                setSearchValueError(message);
                searchRef.current.focus();
            } else if (keys.includes('zipCityValue')) {
                setSearchValueInvalid(false);
                setZipInvalid(true);
                setZipError(message);
                zipRef.current.focus();
            }
        },
        [
            onSubmit,
            placeSuggestions,
            searchValue,
            searchErrorMessage,
            title,
            zipCityValue,
            zipErrorMessage,
            isLocationCategory,
            searchParams,
            setZipInvalid,
        ],
    );

    return (
        <>
            <Grid
                col={{ sm: 4, md: 6, lg: 12 }}
                className={styles[`location-hero__grid`]}
                {...(openPanel ? { inert: '' } : '')}
            >
                <GridColumn
                    colSpan={{ sm: 4, md: 6, lg: 8 }}
                    className={styles[`location-hero__content`]}
                >
                    <Title
                        variant={Title.VARIANT.H2}
                        tag={Title.TAG.H1}
                        content={title}
                        className={styles[`location-hero__title`]}
                    />
                    {description && (
                        <Text
                            variant={Text.VARIANT.T2}
                            tag={Text.TAG.P}
                            content={description}
                            className={styles[`location-hero__description`]}
                        />
                    )}
                    <SearchBar
                        submitHandler={submitForm}
                        data-trigger="search"
                        oneInput={isLocationCategory}
                    >
                        <SearchBar.Inputs>
                            {!isLocationCategory && (
                                <Typeahead
                                    ref={searchRef}
                                    name={inputNameTypeahead}
                                    label={searchLabel}
                                    variant={TextField.VARIANT.NO_BORDER}
                                    buttonProps={undefined}
                                    className={styles[`location-hero__typeahead`]}
                                    items={typeaheadItems}
                                    fixedItem={typeaheadFixedItem}
                                    inputValue={searchValue}
                                    onInputChange={setSearchValue}
                                    onSelectionItemChange={onSelectionItemChange}
                                    isInvalid={searchValueInvalid}
                                    validationErrors={searchValueError}
                                />
                            )}
                            <PlacesTypeahead
                                ref={zipRef}
                                label={zipLabel}
                                inputNameTypeahead={inputNameZipCity}
                                inputValue={zipCityValue}
                                onInputChange={handleZipCityInputChange}
                                onSelectionItemChange={onSelectionZipCityChange}
                                onGeolocationSuccess={setZipCityValue}
                                isInvalid={zipInvalid}
                                validationErrors={zipError}
                                className={styles[`location-hero__typeahead`]}
                                srError={zipSR}
                                googleMaps={googleMaps}
                                autoSelect={{ enabled: true, callback: handleAutoSelect }}
                            />
                        </SearchBar.Inputs>
                        <SearchBar.SubmitButton
                            type="submit"
                            disabled={submitDisabled}
                            className={styles[`location-hero__button`]}
                            data-submit-type="location-search"
                        />
                        <SearchBar.MobileFooter>
                            <Button
                                label={
                                    viewState === 'result-list'
                                        ? smallBreakpoint?.mapView?.label
                                        : smallBreakpoint?.listView?.label
                                }
                                ariaLabel={
                                    viewState === 'result-list'
                                        ? smallBreakpoint?.mapView?.ariaLabel
                                        : smallBreakpoint?.listView?.ariaLabel
                                }
                                buttonStyle={Button.STYLE.SECONDARY}
                                iconComponent={viewState === 'result-list' ? IconMap : IconList}
                                className={styles[`location-hero__button`]}
                                onClick={() => {
                                    viewState === 'result-list' && handleShowMaps();
                                    viewState === 'maps' && handleShowResultList();
                                }}
                            />
                            <Button
                                label={filterLabel || 'Filter'}
                                ariaLabel={filterAriaLabel || 'Filter'}
                                buttonStyle={Button.STYLE.SECONDARY}
                                iconComponent={IconSliders}
                                onClick={filterPanelOpen}
                                className={styles[`location-hero__button`]}
                            />
                        </SearchBar.MobileFooter>
                    </SearchBar>
                </GridColumn>
                {!isMobile && (
                    <GridColumn
                        colSpan={{ sm: 4, md: 6, lg: 3 }}
                        colStart={{ sm: 1, md: 1, lg: 10 }}
                        className={styles[`location-hero__illustration`]}
                    >
                        {!isLocationCategory && illustration?.scene && illustration?.title && (
                            <IllustrationPlayer {...illustration} />
                        )}
                        {isLocationCategory && image && <Image {...image} />}
                    </GridColumn>
                )}
            </Grid>
            {isMobile && <LocationFilter />}
        </>
    );
};

LocationHeroResults.propTypes = {
    /**
     * The title of the location hero component.
     */
    title: PropTypes.string,
    /**
     * The description of the location hero component.
     */
    description: PropTypes.string,
    /**
     * The label for the search input.
     */
    searchLabel: PropTypes.string,
    /**
     * The label for the zip input.
     */
    zipLabel: PropTypes.string,
    /**
     * The illustration to display.
     */
    illustration: PropTypes.object,
};

export default LocationHeroResults;
